import { z } from "zod";

export const reportSchema = z.object({
  id: z.string(),
  overallScore: z.number().nullable(),
  psiOverallScore: z.number().nullable(),
  trustSecurityOverallScore: z.number().nullable(),
  siteAnalysisOverallScore: z.number().nullable(),
  scoringVersion: z.number(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const simplifiedSiteMetadataCheckSchema = z.object({
  id: z.string(),
  title: z.string(),
  locked: z.literal(true),
});

export const siteMetadataCheckSchema = z.union([
  z.object({
    id: z.string(),
    title: z.string(),
    normalized: z.object({ achieved: z.number(), max: z.number() }),
    value: z.any().optional(),
    note: z.string().optional(),
    locked: z.undefined().optional(),
  }),
  simplifiedSiteMetadataCheckSchema,
]);

export const siteMetadataSummarySchema = z.object({
  score: z.number().nullable().optional(),
  checks: z.array(siteMetadataCheckSchema).optional(),
  locked: z.literal(true).optional(),
});

export const pageSpeedDataSchema = z.object({
  scores: z.object({
    performanceScore: z.number().nullable().optional(),
    accessibilityScore: z.number().nullable(),
    bestPracticesScore: z.number().nullable(),
    seoScore: z.number().nullable(),
  }),
  summary: z.unknown().nullable(),
});

export const pageSpeedSchema = z.object({
  mobile: pageSpeedDataSchema.nullable(),
  desktop: pageSpeedDataSchema.nullable(),
});

export const trustSecuritySchema = z.object({
  score: z.number(),
  normalized: z.object({ achieved: z.number(), max: z.number() }),
  checks: z.array(
    z.union([
      z.object({
        id: z.string(),
        title: z.string(),
        normalized: z.object({ achieved: z.number(), max: z.number() }),
        value: z.string().optional(),
        note: z.string().optional(),
        meta: z
          .object({
            domainRatingUpdatedAt: z.string().optional(),
          })
          .catchall(z.unknown())
          .optional(),
      }),
      z.object({
        id: z.string(),
        title: z.string(),
        locked: z.literal(true),
      }),
    ])
  ),
});

export const analyzeDataSchema = z.object({
  url: z.string(),
  createdAt: z.string(),
  siteMetadata: siteMetadataSummarySchema.nullable().optional(),
  pageSpeed: pageSpeedSchema,
  trustSecurity: trustSecuritySchema,
  report: reportSchema,
  userId: z.string().nullable(),
  status: z.string(),
  planType: z.string().optional(),
});

export const analyzeResponseSchema = z.object({
  success: z.boolean(),
  data: analyzeDataSchema.optional(),
  error: z.string().optional(),
  details: z.unknown().optional(),
});

export type AnalyzeData = z.infer<typeof analyzeDataSchema>;
export type AnalyzeResponse = z.infer<typeof analyzeResponseSchema>;
export type ReportData = z.infer<typeof reportSchema>;
export type PageSpeedData = z.infer<typeof pageSpeedDataSchema>;
export type TrustSecurityData = z.infer<typeof trustSecuritySchema>;
export type SiteMetadataSummaryLockedCheck = z.infer<
  typeof simplifiedSiteMetadataCheckSchema
>;
